import {
  Accordion,
  Box,
  Button,
  CloseButton,
  Dialog,
  Portal,
  Presence,
  Span,
  Tag,
  Text,
  Wrap,
} from '@chakra-ui/react';
import { useHistory, useLocation } from 'react-router-dom';
import SearchFiltersBookingOptions from './SearchFiltersBookingOptions';
import SearchFiltersBoolean from './SearchFiltersBoolean';
import SearchFiltersCategory from './SearchFiltersCategory';
import SearchFiltersEnum from './SearchFiltersEnum';
import SearchFiltersMultiEnum from './SearchFiltersMultiEnum';
import SearchFiltersVibes from './SearchFiltersVibes';
import { useEffect, useMemo, useState } from 'react';
import { BeatLoader } from 'react-spinners';
import {
  LISTING_CATEGORIES,
  SPORTS_CLUB_SUB_CATEGORIES,
} from '../../../containers/EditListingPage/listingTypeConfig';
import SearchFiltersPrice from '../../../containers/SearchPage/SearchFiltersPopUp/SearchFiltersPrice';
import SearchFiltersKeyword from '../../../containers/SearchPage/SearchFiltersPopUp/SearchFiltersKeyword';
import { categoryLevel1 } from '../../../containers/SearchPage/SearchFiltersPopUp/SearchFiltersSection';

import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import {
  getActiveFiltersFromSearch,
  removeSingleFilter,
} from '../../../containers/SearchPage/SearchFiltersPopUp/searchFilterUtils';

const BASIC_FILTERS = [
  'categoryLevel',
  'keywords',
  'price',
  'vibesTag',
  'dropInAvailable',
  'accessibilityDetails',
];

const FilterSection = ({ id: key, label, section, showTrigger = true }) => {
  return (
    <Accordion.Item key={key} value={key} py={6}>
      <Accordion.ItemTrigger>
        <Span flex="1">{label}</Span>
        {showTrigger && <Accordion.ItemIndicator />}
      </Accordion.ItemTrigger>
      <Accordion.ItemContent overflow={'visible'}>
        <Accordion.ItemBody>{section}</Accordion.ItemBody>
      </Accordion.ItemContent>
    </Accordion.Item>
  );
};

const ActiveSearchFilters = ({ activeFilters, location, history }) => {
  const hasActiveFilters = !isEmpty(activeFilters);
  return (
    <Presence
      lazyMount
      present={hasActiveFilters}
      animationStyle={{ _open: 'scale-fade-in', _closed: 'scale-fade-out' }}
      animationDuration="moderate"
    >
      <Box
        data-state={hasActiveFilters ? 'open' : 'closed'}
        _open={{
          animation: 'fade-in 300ms ease-out',
        }}
        _closed={{
          animation: 'fade-out 300ms ease-out',
        }}
      >
        <Text fontWeight="semibold" textStyle="lg">
          Selected
        </Text>
        <Wrap gap={'2'}>
          {activeFilters.map((filter, index) => (
            <Tag.Root
              key={index}
              size={'xl'}
              rounded="full"
              variant={'outline'}
              borderColor={'gray.900'}
              borderWidth={'0.15rem'}
              color={'almostBlack'}
              py={2}
              fontWeight={'550'}
            >
              <Tag.Label>{filter.displayValue}</Tag.Label>
              <Tag.EndElement cursor="pointer">
                <Tag.CloseTrigger
                  onClick={() => removeSingleFilter(filter, location, history)}
                  cursor="pointer"
                />
              </Tag.EndElement>
            </Tag.Root>
          ))}
        </Wrap>
      </Box>
    </Presence>
  );
};

const SearchFiltersPopUp = ({
  isOpen,
  setIsOpen,
  totalItems,
  searchInProgress,
  availableFilters,
  updateQueryParams,
  onClose,
}) => {
  const history = useHistory();
  const location = useLocation();

  const [selectedCategory, setSelectedCategory] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const category = params.get(categoryLevel1);

    setSelectedCategory(category || null);

    setActiveFilters(getActiveFiltersFromSearch(location.search, availableFilters));
  }, [location.search]);

  const customFilters = useMemo(
    () => availableFilters.filter(filter => !BASIC_FILTERS.includes(filter.key)),
    [availableFilters]
  );

  const clearAllFilters = () => {
    const params = new URLSearchParams(location.search);

    // Remove all public (filter) params
    [...params.keys()].forEach(key => {
      if (key.startsWith('pub_') || key === 'price' || key === 'keywords') {
        params.delete(key);
      }
    });

    history.push({
      pathname: location.pathname,
      search: params.toString(),
    });
  };
  const handleClose = () => {
    !isNil(onClose) ? onClose() : setIsOpen(!isOpen);
  };

  return (
    <Dialog.Root
      open={isOpen}
      scrollBehavior="inside"
      size={{ mdDown: 'full', md: 'lg' }}
      motionPreset="slide-in-bottom"
      onOpenChange={handleClose}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content rounded={{ mdDown: '0', md: '3xl' }}>
            <Dialog.Header
              display={'flex'}
              align={'center'}
              justifyContent={'center'}
              borderBottomWidth={'0.1rem'}
              textAlign={'center'}
              pt={0}
              pb={0}
            >
              <Dialog.Title>
                <Text fontWeight="semibold" textStyle="lg" m={0} p={0}>
                  Filters
                </Text>
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Box>
                <ActiveSearchFilters
                  activeFilters={activeFilters}
                  history={history}
                  location={location}
                />
                <Accordion.Root
                  collapsible
                  multiple
                  size={'lg'}
                  defaultValue={[
                    'keywords',
                    'activeFilters',
                    'category',
                    'bookingOptions',
                    'vibeTags',
                  ]}
                >
                  <FilterSection
                    key={'keywords'}
                    id={'keywords'}
                    label={'Search'}
                    showTrigger={false}
                    section={<SearchFiltersKeyword updateQueryParams={updateQueryParams} />}
                  />
                  <FilterSection
                    key={'category'}
                    id={'category'}
                    label={'Category'}
                    showTrigger={false}
                    section={
                      <SearchFiltersCategory updateQueryParams={updateQueryParams} showHeader />
                    }
                  />
                  <FilterSection
                    key={'bookingOptions'}
                    id={'bookingOptions'}
                    label={'Booking options'}
                    showTrigger={false}
                    section={<SearchFiltersBookingOptions updateQueryParams={updateQueryParams} />}
                  />
                  <FilterSection
                    key={'vibeTags'}
                    id={'vibeTags'}
                    label={'Vibes'}
                    section={<SearchFiltersVibes updateQueryParams={updateQueryParams} />}
                  />
                  {selectedCategory === LISTING_CATEGORIES.SPORTS_CLUB.id && (
                    <FilterSection
                      key={'typeOfSport'}
                      id={'typeOfSport'}
                      label={'Type of sport'}
                      section={
                        <SearchFiltersEnum
                          id={'categoryLevel2'}
                          updateQueryParams={updateQueryParams}
                          enumOptions={SPORTS_CLUB_SUB_CATEGORIES}
                        />
                      }
                    />
                  )}
                  {customFilters.map(filter => {
                    let section = null;
                    if (filter.schemaType === 'multi-enum') {
                      section = (
                        <SearchFiltersMultiEnum
                          key={filter.key}
                          id={filter.key}
                          enumOptions={filter.enumOptions}
                          filterConfig={filter.filterConfig}
                          updateQueryParams={updateQueryParams}
                        />
                      );
                    } else if (filter.schemaType === 'enum') {
                      section = (
                        <SearchFiltersEnum
                          key={filter.key}
                          id={filter.key}
                          enumOptions={filter.enumOptions}
                          updateQueryParams={updateQueryParams}
                        />
                      );
                    } else if (filter.schemaType === 'boolean') {
                      section = (
                        <SearchFiltersBoolean
                          key={filter.key}
                          id={filter.key}
                          filterConfig={filter.filterConfig}
                          updateQueryParams={updateQueryParams}
                        />
                      );
                    }
                    return (
                      section && (
                        <FilterSection
                          key={filter.key}
                          id={filter.key}
                          label={filter.filterConfig?.label}
                          section={section}
                        />
                      )
                    );
                  })}
                  <FilterSection
                    key={'price'}
                    id={'price'}
                    label={'Price'}
                    section={<SearchFiltersPrice updateQueryParams={updateQueryParams} />}
                  />
                </Accordion.Root>

                {isEmpty(customFilters) && (
                  <Text color="lightGrayText" fontWeight="medium" textStyle="sm" mt={'1rem'}>
                    Select a category to see more specific filters
                  </Text>
                )}
              </Box>
            </Dialog.Body>
            <Dialog.Footer justifyContent={'space-between'} shadow={'xl'}>
              <Button variant="plain" onClick={clearAllFilters}>
                Clear all
              </Button>
              <Button
                loading={searchInProgress}
                spinner={<BeatLoader size={8} color="white" />}
                onClick={handleClose}
                rounded={'xl'}
                className={'plausible-event-name=Search+Filters+Pop+Up+Show+Results+Button'}
              >
                {totalItems === 1 ? `Show ${totalItems} listing` : `Show ${totalItems} listings`}
              </Button>
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default SearchFiltersPopUp;
